import {
  IApplyToAllProductsCategoryReq,
  IApplyToAllProductsReq,
} from "@evennt-tech/api-v2-external";

export type ProductsApplyToCategory = IApplyToAllProductsCategoryReq;

export type ProductsApplyTo = IApplyToAllProductsReq;

type ResolveProductsSelectionArgs<T> = {
  selectedItems: T[];
  loadedProductIds: Set<string>;
  totalCount: number;
  categoryId?: string;
  categoryIds?: string[];
  search?: string;
  deselectedProductIds?: string[];
};

export type ProductsSelection<T> = {
  items: T[];
  applyTo: ProductsApplyTo;
  targetCount: number;
};

export const isApplyToAllSelection = (applyTo: ProductsApplyTo): boolean =>
  !!applyTo.applyToAll ||
  !!applyTo.categories?.some(({ applyToAll }) => applyToAll);

export const resolveProductsSelection = <
  T extends { id: string; name?: string }
>({
  selectedItems,
  loadedProductIds,
  totalCount,
  categoryId,
  categoryIds,
  search,
  deselectedProductIds,
}: ResolveProductsSelectionArgs<T>): ProductsSelection<T> => {
  const selectedIds = new Set(selectedItems.map(({ id }) => id));
  const items = selectedItems.filter(({ name }) => !!name);
  const selectedPlaceholderRows = selectedItems.length - items.length;

  const scopeCategoryIds = categoryId ? [categoryId] : categoryIds ?? [];

  if (!selectedPlaceholderRows) {
    const productIds = items.map(({ id }) => id);

    return {
      items,
      applyTo: categoryId
        ? { categories: [{ categoryId, productIds }] }
        : { productIds },
      targetCount: items.length,
    };
  }

  const excludedProductIds =
    deselectedProductIds ??
    Array.from(loadedProductIds).filter((id) => !selectedIds.has(id));

  const scope = {
    applyToAll: true,
    ...(search && { applyToAllSearch: search }),
    ...(excludedProductIds.length && { excludedProductIds }),
  };

  return {
    items,
    applyTo: scopeCategoryIds.length
      ? {
          categories: scopeCategoryIds.map((id) => ({
            categoryId: id,
            ...scope,
          })),
        }
      : scope,
    targetCount: Math.max(totalCount - excludedProductIds.length, 0),
  };
};
